/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useServerlessFeatureFlag } from '@modules/launchdarkly'
import usePermissions from '@modules/permissions-lib/hooks'
import { useListElasticsearchProjectsQuery } from '@modules/project-user-lib/hooks/list'

import type { UseQueryOptions } from 'react-query'
import type { ResourceQueryResult } from '../types'

export const useResourcesQuery = (
  options?: Pick<UseQueryOptions, 'enabled'>,
): ResourceQueryResult => {
  const enabled = options?.enabled ?? true

  const isServerlessEnabled = useServerlessFeatureFlag()

  const hasListElasticsearchPermission = usePermissions(
    [{ type: 'project-elasticsearch', action: 'list' }],
    'every',
    {
      enabled: isServerlessEnabled && enabled,
    },
  ).hasPermissions

  const elasticsearchQuery = useListElasticsearchProjectsQuery({
    enabled: hasListElasticsearchPermission,
  })

  return {
    resourceType: 'elasticsearch',
    data: elasticsearchQuery.data?.items,
    isLoading: elasticsearchQuery.isLoading,
    error: elasticsearchQuery.error,
    isEnabled: hasListElasticsearchPermission,
  }
}
